import Locations from "@/managers/location/locations"
import PublicServiceConnection from "@/managers/data/serviceConnection/publicServiceConnection"
import State from "@/store/location/services"
import Portal from "@/managers/portal"
import List from "@/managers/base/list"
import Authentication from "@/managers/facebook/authentication"
import {when} from "@/store/store"
import User from "../user/user"
import DetailedService from "@/managers/data/serviceConnection/detailedService"
import {globalEvents, sendGlobalEvent} from "@/managers/session/globalEvents"
import type ServiceConnection from "@/managers/data/serviceConnection/serviceConnection"
import Publishers from "@/managers/organization/publishers"

class Services extends List<DetailedService, number> {
	public get hasAnyWorking(): boolean {
		return this.isReady && this.list!.some(s => s.connections.length > 0)
	}

	protected get dependent(): number | null {
		return Locations.main !== null ? Locations.main.id : null
	}

	protected get state(): typeof State {
		return State
	}

	constructor() {
		super("service connections")
	}

	public addFacebook(id: string): Promise<PublicServiceConnection> {
		return this.addMetaServiceToLocation(id, Publishers.facebookId, this.dependent!)
	}

	public addInstagram(id: string): Promise<PublicServiceConnection> {
		return this.addMetaServiceToLocation(id, Publishers.instagramId, this.dependent!)
	}

	public async addMetaServiceToLocation(id: string, publisherId: number, locationId: number): Promise<PublicServiceConnection> {
		if (Authentication.authResponse === null)
			throw new Error("No Facebook auth response available")

		return this.addServiceToLocation(id, publisherId, locationId, Authentication.authResponse.accessToken, Authentication.authResponse.userID)
	}

	public async addCustomServiceToLocation(id: string, publisherId: number, locationId: number): Promise<PublicServiceConnection> {
		return this.addServiceToLocation(id, publisherId, locationId, "")
	}

	public async addServiceToLocation(id: string, publisherId: number, locationId: number, key: string, serviceUserId?: string): Promise<PublicServiceConnection> {
		const newConnection = await Portal.call.locations.serviceConnectionsPost(locationId, publisherId, id, key, serviceUserId).response
			.then(PublicServiceConnection.fromApi)

		await this.updateList()

		sendGlobalEvent(globalEvents.locationServiceChanged, locationId, publisherId)

		if (this.dependent === locationId) {
			const service = this.list!.find(s => s.publisherId === publisherId)
			if (service && User.isReady)
				return service.connections.find(c => c.user.id === User.current!.id) ?? newConnection
		}

		return newConnection
	}

	public async deleteFromLocation(locationId: number, publisherId: number, all: boolean): Promise<void> {
		await Portal.call.locations.serviceConnectionsDelete(locationId, publisherId, all).response

		if (locationId === this.dependent) {
			const serviceIndex = this.list!.findIndex(service => service.publisherId === publisherId)

			if (serviceIndex !== -1) {
				if (all)
					this.state.remove(serviceIndex)
				else if(User.isReady) {
					const connectionIndex = this.list![serviceIndex].connections
						.findIndex(connection => connection.user.id === User.current!.id)

					if (connectionIndex !== -1)
						this.state.removeConnection({serviceIndex, connectionIndex})
				}
			}
		}

		sendGlobalEvent(globalEvents.locationServiceChanged, locationId, publisherId)
	}

	public async deleteConnection(locationId: number, connection: ServiceConnection): Promise<void> {
		await Portal.call.serviceConnections.delete(connection.id).response

		if (locationId === this.dependent) {
			const serviceIndex = this.list!.findIndex(service => service.publisherId === connection.publisherId)

			if (serviceIndex !== -1) {
				const connectionIndex = this.list![serviceIndex].connections
					.findIndex(existingConnection => existingConnection.id === connection.id)

				if (connectionIndex !== -1)
					this.state.removeConnection({serviceIndex, connectionIndex})
			}
		}

		sendGlobalEvent(globalEvents.locationServiceChanged, locationId, connection.publisherId)
	}

	public async loadFromLocation(id: number): Promise<DetailedService[]> {
		if (this.dependent === id) {
			await when(() => this.isReady)
			return this.list!
		}
		return this.apiLoad(id)
	}

	protected apiLoad(dependent: number): Promise<DetailedService[]> {
		return Portal.call.locations.servicesGet(dependent)
			.response.then(locations => locations.map(DetailedService.fromApi))
	}
}

export {PublicServiceConnection, DetailedService}
export default new Services()
